/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useCheckout, useAuth } from "../../providers";
import { useLocalization } from "@lemmonhq/uikit";
import {
  calculateCheckoutChargeAmount,
  calculateCheckoutSubTotal,
  calculateCheckoutTotal,
  calculateLegScopedPromotionsTotal,
  round,
  calculateTableCreditAmount,
} from "@lemmonhq/orders";
import {
  buildCheckoutDiscount,
  DiscountBreakdownModal,
  getDiscountComponents,
} from "./DiscountBreakdownModal";

export type TransactionSummaryProps = {};

export const TransactionSummary = defineComponent<TransactionSummaryProps>({
  name: "TransactionSummary",
  props: [],
  setup(props) {
    const checkout = useCheckout();
    const auth = useAuth();
    const { t } = useLocalization();
    const showDiscountBreakdown = ref(false);

    const total = computed(() =>
      checkout.checkout.value
        ? calculateCheckoutTotal(checkout.checkout.value)
        : 0,
    );

    // When a split/partial leg is armed in the editor, the big number shows the
    // amount charged in THIS payment (net of any discount/promotion/gift card
    // applied to the leg), with the whole order's gross underneath.
    //
    // Uses the SAME helper the provider adapters charge with
    // (calculateCheckoutChargeAmount) rather than re-deriving it here, so what
    // the cashier reads can never differ from what the terminal takes — the
    // local copy this replaces omitted the "reductions can't go below zero"
    // floor, so an over-large discount displayed a negative amount while the
    // charge floored at 0.
    const stagedIntent = computed(
      () => checkout.checkout.value?.staffCheckout?.intent,
    );
    const chargeNow = computed(() =>
      checkout.checkout.value
        ? calculateCheckoutChargeAmount(checkout.checkout.value)
        : 0,
    );
    const removeDiscountComponent = (index: number) => {
      if (!checkout.checkout.value) return;
      const existing = getDiscountComponents(checkout.checkout.value.discount);
      const next = existing.filter((_, i) => i !== index);
      checkout.setCheckout({
        ...checkout.checkout.value,
        discount: buildCheckoutDiscount(next),
      });
      if (next.length === 0) showDiscountBreakdown.value = false;
    };

    return () => {
      if (!checkout.checkout.value) return null;

      const cd = checkout.checkout.value;
      const { discount, tip, giftCards } = cd;
      const subtotal = calculateCheckoutSubTotal(cd);
      const currency = auth.user.value?.restaurant.currency || "CHF";
      const n2 = (n: number) => n.toFixed(2);

      // Split/partial progress. Coverage is counted on each leg's GROSS
      // `subtotal` (not the net cash), so a discounted leg still reduces the
      // remaining balance by its full share — matching the finalize trigger.
      const legs = cd.staffCheckout?.payments ?? [];
      const paidGross = legs.reduce((s, p) => s + p.subtotal, 0);
      const remainingGross = round(subtotal - paidGross);
      const staged = !!stagedIntent.value;
      const inSplitFlow = staged || legs.length > 0;

      // Adjustments for the CURRENT context (this leg when staged, else the
      // whole cart). `gross` is what's owed before reductions; `chargeNow` is
      // the net the big number shows.
      const gross = staged ? stagedIntent.value!.amount : subtotal;
      const discountAmt = discount?.amount || 0;
      const promoAmt = calculateLegScopedPromotionsTotal(cd);
      const giftAmt = (giftCards || []).reduce((s, g) => s + g.amount, 0);
      const tipAmt = tip?.value || 0;
      // Table credit is taken first and cannot be removed
      // (docs/plans/table_credit/pos_checkout_credit.md).
      const creditAmt = calculateTableCreditAmount(cd);
      const reductions = discountAmt + promoAmt + giftAmt + creditAmt;

      // Breakdown chips. When a reduction applies, the gross (this leg, or the
      // whole cart) leads the row as the reference the reductions come off of;
      // reductions are success green, the tip neutral. The discount chip still
      // opens its removable breakdown.
      const adjustments = [
        reductions > 0 && (
          <span class="text-base-content/60">
            {currency} {n2(gross)}
          </span>
        ),
        discountAmt > 0 && (
          <button
            type="button"
            class="text-success underline underline-offset-2 decoration-dotted hover:opacity-80 transition-opacity"
            onClick={() => (showDiscountBreakdown.value = true)}
            title="View or remove discount"
          >
            {n2(discountAmt)} {t("checkout.summary.discount")}
            {discount?.category === "fork" ? " (fork)" : null}
          </button>
        ),
        promoAmt > 0 && (
          <span class="text-success">
            {n2(promoAmt)} {t("checkout.summary.promo")}
          </span>
        ),
        giftAmt > 0 && (
          <span class="text-success">
            {n2(giftAmt)} {t("checkout.summary.gift")}
          </span>
        ),
        creditAmt > 0 && (
          <span class="text-success">
            {n2(creditAmt)} {t("pos.table_credit.title")}
          </span>
        ),
        tipAmt > 0 && (
          <span class="text-base-content/60">
            +{n2(tipAmt)} {t("checkout.summary.tip")}
          </span>
        ),
      ].filter(Boolean) as unknown[];

      const pct = (nn: number) =>
        subtotal > 0 ? Math.max(0, Math.min(100, (nn / subtotal) * 100)) : 0;
      const paidPct = pct(paidGross);
      const legPct = staged ? Math.min(pct(gross), 100 - paidPct) : 0;

      return (
        <>
          <div class="text-2xl">{staged ? "PAYING NOW" : "TOTAL"}</div>

          {/* Net amount charged; the gross is shown in the breakdown row below */}
          <div class="text-6xl text-accent font-bold">
            {currency} {chargeNow.value.toFixed(2)}
          </div>

          {/* Adjustment breakdown, interleaved with dot separators */}
          {adjustments.length > 0 && (
            <div class="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm">
              {adjustments.map((node, i) => (
                <span key={i} class="flex items-center gap-2">
                  {i > 0 && <span class="text-base-content/30">·</span>}
                  {node}
                </span>
              ))}
            </div>
          )}

          {inSplitFlow ? (
            <div class="mx-auto mt-4 w-full max-w-[18rem]">
              <div class="mb-3 border-t border-base-content/10" />
              {/* Order progress: paid (solid) + this leg (lighter) of subtotal */}
              <div class="flex h-2 w-full overflow-hidden rounded-full bg-base-300/70">
                <div
                  class="h-full bg-success transition-all duration-300"
                  style={{ width: `${paidPct}%` }}
                />
                <div
                  class="h-full bg-success/40 transition-all duration-300"
                  style={{ width: `${legPct}%` }}
                />
              </div>
              <div class="mt-1 flex flex-wrap items-center justify-center gap-x-2 text-sm text-base-content/60">
                <span>
                  <span class="font-medium text-success">{n2(paidGross)}</span>
                  {" / "}
                  {n2(subtotal)} {t("tables.checkout.paid")}
                </span>
                <span class="text-base-content/30">·</span>
                <span>
                  {n2(remainingGross)} {t("tables.checkout.remaining")}
                </span>
              </div>
            </div>
          ) : (
            <div class="mt-2 text-1xl text-center text-accent">
              SUB-TOTAL = {n2(subtotal)}
            </div>
          )}

          {showDiscountBreakdown.value && discount && (
            <DiscountBreakdownModal
              discount={discount}
              onClose={() => (showDiscountBreakdown.value = false)}
              onRemoveComponent={removeDiscountComponent}
            />
          )}
        </>
      );
    };
  },
});
